import { useState, type FormEvent } from "react";
import { Link } from "wouter";
import { UserPlus, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { ApiError, type ClaimRequest } from "@/lib/api";
import { useClaimPaiement } from "@/lib/backoffice-hooks";
import { setToken as setAuthToken } from "@/lib/auth";

/**
 * Carte « Créer mon compte » (claim) affichée sur la page de suivi d'un
 * paiement invité, lorsque le statut expose `canClaim === true`.
 *
 * Le parent invité fournit un mot de passe (et confirme ses coordonnées) ;
 * le paiement et l'enfant saisis pendant le parcours sont rattachés au compte
 * nouvellement créé via POST /api/public/paiements/{token}/claim.
 *
 * Si le backend renvoie un jeton de session (`accessToken`), on connecte
 * directement l'utilisateur et on l'oriente vers son tableau de bord ; sinon
 * on l'invite à se connecter.
 */
interface ClaimAccountCardProps {
  token: string;
  /** Email du payeur (pré-rempli, éditable). */
  payerEmail?: string | null;
  /** Téléphone du payeur (pré-rempli). */
  payerTelephone?: string | null;
  /** Prénoms du payeur (pré-rempli). */
  payerPrenoms?: string | null;
  /** Nom du payeur (pré-rempli). */
  payerNom?: string | null;
}

/** Évalue grossièrement la robustesse d'un mot de passe (0 → 4). */
function passwordScore(pwd: string): number {
  let score = 0;
  if (pwd.length >= 8) score += 1;
  if (pwd.length >= 12) score += 1;
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score += 1;
  if (/\d/.test(pwd) || /[^A-Za-z0-9]/.test(pwd)) score += 1;
  return score;
}

const STRENGTH_LABELS = ["", "Faible", "Moyen", "Bon", "Excellent"] as const;
const STRENGTH_COLORS = [
  "bg-transparent",
  "bg-destructive",
  "bg-gold",
  "bg-sage",
  "bg-sage",
] as const;

const CORAL_BUTTON = "bg-coral hover:bg-[#d16b50] text-white font-bold";

export default function ClaimAccountCard({
  token,
  payerEmail,
  payerTelephone,
  payerPrenoms,
  payerNom,
}: ClaimAccountCardProps) {
  const claim = useClaimPaiement(token);

  const [email, setEmail] = useState<string>(payerEmail ?? "");
  const [prenom, setPrenom] = useState<string>(payerPrenoms ?? "");
  const [nom, setNom] = useState<string>(payerNom ?? "");
  const [password, setPassword] = useState<string>("");
  const [confirm, setConfirm] = useState<string>("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [done, setDone] = useState<boolean>(false);
  const [loggedIn, setLoggedIn] = useState<boolean>(false);

  const score = passwordScore(password);
  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setLocalError(null);

    if (!email.trim()) {
      setLocalError("Veuillez renseigner votre email.");
      return;
    }
    if (!nom.trim() || !prenom.trim()) {
      setLocalError("Veuillez renseigner vos nom et prénoms.");
      return;
    }
    if (password.length < 8) {
      setLocalError("Le mot de passe doit comporter au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setLocalError("Les deux mots de passe ne correspondent pas.");
      return;
    }

    const body: ClaimRequest = {
      password,
      email: email.trim(),
      telephone: (payerTelephone ?? "").trim(),
      prenom: prenom.trim(),
      nom: nom.trim(),
    };

    claim.mutate(
      { data: body },
      {
        onSuccess: (res) => {
          setDone(true);
          // Le backend peut renvoyer un jeton de session — on connecte alors
          // directement l'utilisateur. Le champ n'est pas typé dans
          // ClaimResponse, on le lit défensivement.
          const accessToken = (res as { accessToken?: unknown }).accessToken;
          if (typeof accessToken === "string" && accessToken.length > 0) {
            setAuthToken(accessToken);
            setLoggedIn(true);
          }
        },
        onError: (err: unknown) => {
          // Le toast d'erreur est déjà émis par le hook ; on affiche en plus un
          // message clair et actionnable selon le code HTTP.
          if (err instanceof ApiError) {
            if (err.status === 409) {
              setLocalError(
                "Un compte existe déjà avec cet email. Connectez-vous plutôt.",
              );
              return;
            }
            if (err.status === 400) {
              setLocalError(
                err.message ||
                  "Certaines informations sont invalides. Vérifiez le formulaire.",
              );
              return;
            }
            if (err.status === 502 || err.status === 503) {
              setLocalError(
                "Le service de comptes est momentanément indisponible. Réessayez dans quelques instants.",
              );
              return;
            }
            setLocalError(err.message);
            return;
          }
          setLocalError(
            err instanceof Error ? err.message : "Une erreur est survenue.",
          );
        },
      },
    );
  }

  if (done) {
    return (
      <div className="mt-4 p-6 bg-sage/5 border border-sage/20 rounded-2xl flex flex-col items-center text-center">
        <CheckCircle2 className="text-sage w-12 h-12 mb-3" />
        <h3 className="font-bold text-navy mb-1">Compte créé</h3>
        <p className="text-sm text-navy/60 mb-4 max-w-md">
          {loggedIn
            ? "Votre espace est prêt. Vous êtes connecté : retrouvez vos reçus et vos paiements à tout moment."
            : "Votre espace est prêt. Connectez-vous pour retrouver vos reçus et vos paiements à tout moment."}
        </p>
        <Link href={loggedIn ? "/dashboard" : "/auth/login"} className="w-full sm:w-auto">
          <Button className={`w-full sm:w-auto ${CORAL_BUTTON}`}>
            {loggedIn ? "Accéder à mon espace" : "Se connecter"}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="mt-4 p-6 bg-coral/5 border border-coral/20 rounded-2xl">
      <div className="flex items-start gap-4 mb-5">
        <UserPlus className="text-coral w-8 h-8 flex-shrink-0" />
        <div>
          <h3 className="font-bold text-navy mb-1">Créez votre espace parent</h3>
          <p className="text-sm text-navy/60">
            Retrouvez vos reçus et payez plus vite la prochaine fois. Votre
            paiement et l'enfant que vous venez de saisir seront automatiquement
            rattachés à votre compte.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="claim-email">Email</Label>
          <Input
            id="claim-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vous@exemple.ci"
            autoComplete="email"
            className="mt-1"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="claim-prenom">Prénoms</Label>
            <Input
              id="claim-prenom"
              value={prenom}
              onChange={(e) => setPrenom(e.target.value)}
              placeholder="Prénoms"
              autoComplete="given-name"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="claim-nom">Nom</Label>
            <Input
              id="claim-nom"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              placeholder="Nom"
              autoComplete="family-name"
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="claim-password">Mot de passe (8 caractères min.)</Label>
          <PasswordInput
            id="claim-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Choisissez un mot de passe"
            autoComplete="new-password"
            className="mt-1"
            aria-invalid={tooShort}
          />
          {password.length > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 grid grid-cols-4 gap-1">
                {[1, 2, 3, 4].map((i) => (
                  <span
                    key={i}
                    className={`h-1.5 rounded-full ${
                      i <= score ? STRENGTH_COLORS[score] : "bg-orange-100"
                    }`}
                  />
                ))}
              </div>
              <span className="text-xs font-bold text-navy/60 w-16 text-right">
                {STRENGTH_LABELS[score]}
              </span>
            </div>
          )}
          {tooShort && (
            <p className="mt-1 text-xs text-destructive">
              Au moins 8 caractères requis.
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="claim-confirm">Confirmez le mot de passe</Label>
          <PasswordInput
            id="claim-confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Retapez le mot de passe"
            autoComplete="new-password"
            className="mt-1"
            aria-invalid={mismatch}
          />
          {mismatch && (
            <p className="mt-1 text-xs text-destructive">
              Les mots de passe ne correspondent pas.
            </p>
          )}
        </div>

        {localError && (
          <div className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-xl px-4 py-3">
            {localError}
          </div>
        )}

        <Button
          type="submit"
          disabled={claim.isPending}
          className={`w-full ${CORAL_BUTTON}`}
        >
          {claim.isPending ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" /> Création en cours…
            </>
          ) : (
            "Créer mon espace"
          )}
        </Button>

        <p className="flex items-center justify-center gap-1.5 text-xs text-navy/50">
          <ShieldCheck size={14} className="text-sage" />
          Vous avez déjà un compte ?{" "}
          <Link href="/auth/login" className="font-bold text-coral hover:underline">
            Se connecter
          </Link>
        </p>
      </form>
    </div>
  );
}
